import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson, ApiProblemError } from '../../../lib/apiFetchJson';
import { ANALYTICS_EVENTS } from '@shared/analytics/events';
import { useTrackSignalsEvent } from './signalsAnalytics';
import { invalidateFlowList } from '../flow-list-invalidation';
import { FLOW_CONTACT_SIGNALS_KEY, FLOW_ACCOUNT_SIGNALS_KEY } from '../contact-state-keys';
import {
  ArchiveSignalActionBodySchema,
  SnoozeResultSchema,
  UnsnoozeResultSchema,
  ArchiveResultSchema,
  type ArchiveSignalActionBody,
  type SnoozeSignalActionBody,
  type SnoozeResult,
  type UnsnoozeResult,
  type ArchiveResult,
} from '@shared/schemas/terra-signals';

/**
 * `useSnoozeSignal` / `useUnsnoozeSignal` / `useArchiveSignal` (bead .15,
 * U6). Query/mutation split per `EnrollQueue/useEnrollQueueQueries.ts`.
 *
 * Discovery: the bead body and Behavior Spec 3 both describe the snooze
 * request body as `{ until: <iso> }`. The ACTUAL wire contract
 * (`SnoozeSignalActionBodySchema`, `src/shared/schemas/terra-signals.ts:338`,
 * and `flow-signals.ts`'s snooze handler which reads
 * `parsed.data.snoozeUntil`) names the field `snoozeUntil`, not `until`. This
 * module sends `snoozeUntil` — the real server contract wins over the
 * English description of it.
 *
 * Plan `2026-09-25-003-pr1683-premerge-fixes-plan.md`, U4a: every mutation
 * below now goes through `fetchJson`/`ApiProblemError`
 * (`lib/apiFetchJson.ts`, U3) instead of its own hand-rolled `!res.ok` /
 * response-body handling. `SignalActionError`, `SignalActionProblemSchema`,
 * `GENERIC_FALLBACK_TITLE`, `NETWORK_FALLBACK_TITLE`, `fetchOrNetworkError`,
 * `rejectOnFailure`, and `parseSuccessBody` are retired — `ApiProblemError`
 * (promoted from `SignalActionError`) now carries the same
 * `status`/`title`/`detail`/`code` shape callers already render, plus a
 * digit-free `message` (fail-closed on a malformed body, network/abort
 * mapped to the shared sentence). Renderers that read `.title` directly
 * (`SnoozePopover`, `SignalRowActions`, `SignalActionArchiveForm`) now fall
 * back to `.message` since `.title` is optional on `ApiProblemError` (unset
 * for network/aborted failures, where the old class always synthesized one).
 */

/**
 * The three surfaces a signal's verdict change can touch: the Signals tab
 * list, a contact's Actions-tab timeline, AND the account's Timeline (which
 * merges in that same contact's entity-signal rows as action rows,
 * `AccountTimeline`). Exported (fix-plan-batch2.md D-D / U7) so
 * `useRefreshSignals.ts` invalidates the identical set after a live check —
 * one invalidation set for "a signal's verdict may have changed," used by
 * every mutation AND the refresh path.
 */
export async function invalidateSignalQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    invalidateFlowList(queryClient, 'signal'),
    queryClient.invalidateQueries({ queryKey: FLOW_CONTACT_SIGNALS_KEY }),
    queryClient.invalidateQueries({ queryKey: FLOW_ACCOUNT_SIGNALS_KEY }),
  ]);
}

/**
 * Round-1 fix D-J. Auto-expand-next used to ride a per-`mutate()`
 * `onSuccess` supplied by the ROW component. TanStack v5 runs per-mutate
 * callbacks only while the observer that called `mutate()` is still
 * subscribed — and the list refetch this very mutation triggers unmounts
 * that row, so the callback was silently skipped exactly when it mattered.
 *
 * Hook-level `onSuccess` runs on the Mutation itself, so it always fires;
 * and calling `onActed` FIRST, then `void`-ing the invalidation (never
 * returning/awaiting it), means the caller computes "the next row" against
 * the list as the rep still sees it, not the post-refetch one.
 *
 * Per-`mutate()` callbacks keep only their presentation duties
 * (`onClose`, inline `onError`).
 */
export interface SignalMutationOptions {
  onActed?: (signalId: string) => void;
}

/**
 * `signalType` / `stepType` / `stepNumber` are the row context for the restored Action View
 * analytics. They ride the mutation VARIABLES (not per-`mutate()` callbacks) because the event
 * is emitted from the hook-level `onSuccess`, which — unlike a per-mutate callback — still runs
 * after the calling component unmounts (auto-expand-next, navigation). Required: the one caller
 * always has the row, and an omitted field would silently emit a null dimension.
 */
export interface SnoozeSignalVariables {
  signalId: string;
  signalType: string | null;
  stepType: string | null;
  stepNumber: number | null;
  /** ISO date string. Omitted -> the "Mark as read" popover's "Indefinitely" option — the snooze lifts when the watched Gong step changes. */
  snoozeUntil?: string;
}

export function useSnoozeSignal(opts?: SignalMutationOptions) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const trackSignalsEvent = useTrackSignalsEvent();
  return useMutation<SnoozeResult, ApiProblemError, SnoozeSignalVariables>({
    meta: { suppressErrorToast: true },
    mutationFn: async ({ signalId, snoozeUntil }) => {
      const body: SnoozeSignalActionBody = snoozeUntil ? { snoozeUntil } : {};
      return fetchJson(apiFetch, SnoozeResultSchema, `/api/flow/signals/${encodeURIComponent(signalId)}/snooze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    onSuccess: (_data, vars) => {
      // Success only (a 409/needs_date never reaches here). `snoozeUntil`
      // undefined is the wire's "Indefinitely".
      trackSignalsEvent(ANALYTICS_EVENTS.ACTION_MARKED_COMPLETED, {
        signalId: vars.signalId,
        signalType: vars.signalType,
        stepType: vars.stepType,
        stepNumber: vars.stepNumber,
        snoozeKind: vars.snoozeUntil === undefined ? 'indefinite' : 'dated',
      });
      opts?.onActed?.(vars.signalId);
      void invalidateSignalQueries(queryClient);
    },
  });
}

export interface UnsnoozeSignalVariables {
  signalId: string;
}

export function useUnsnoozeSignal(opts?: SignalMutationOptions) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation<UnsnoozeResult, ApiProblemError, UnsnoozeSignalVariables>({
    meta: { suppressErrorToast: true },
    mutationFn: async ({ signalId }) =>
      fetchJson(apiFetch, UnsnoozeResultSchema, `/api/flow/signals/${encodeURIComponent(signalId)}/snooze`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, vars) => {
      opts?.onActed?.(vars.signalId);
      void invalidateSignalQueries(queryClient);
    },
  });
}

export type ArchiveSignalVariables = ArchiveSignalActionBody & {
  signalId: string;
  /** The signal's `signal_type`, for the `Action Archived` event (rides the variables for the same reason as `SnoozeSignalVariables`). */
  signalType: string | null;
};

export function useArchiveSignal(opts?: SignalMutationOptions) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const trackSignalsEvent = useTrackSignalsEvent();
  return useMutation<ArchiveResult, ApiProblemError, ArchiveSignalVariables>({
    meta: { suppressErrorToast: true },
    mutationFn: async ({ signalId, reason, feedback }) => {
      const body: ArchiveSignalActionBody = { reason, feedback };
      const parsedBody = ArchiveSignalActionBodySchema.safeParse(body);
      if (!parsedBody.success) {
        throw new ApiProblemError({
          kind: 'malformed',
          status: 0,
          code: 'INVALID_REQUEST',
          title: 'Archiving needs a reason and feedback.',
          userMessage: 'Archiving needs a reason and feedback.',
        });
      }
      return fetchJson(apiFetch, ArchiveResultSchema, `/api/flow/signals/${encodeURIComponent(signalId)}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsedBody.data),
      });
    },
    onSuccess: (_data, vars) => {
      trackSignalsEvent(ANALYTICS_EVENTS.ACTION_ARCHIVED, {
        signalId: vars.signalId,
        reason: vars.reason,
        signalType: vars.signalType,
      });
      opts?.onActed?.(vars.signalId);
      void invalidateSignalQueries(queryClient);
    },
  });
}
